import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import Study from '@/components/Study.vue'
import Stats from '@/components/Stats.vue'
import SignIn from '@/components/SignIn.vue'
import Register from '@/components/Register.vue'
import UtiDiscountPage from '@/components/UtiDiscountPage.vue'
import EmailAuth from '@/components/EmailAuth.vue'
import Invite from '@/components/Invite.vue'
import ForgotPassword from '@/components/ForgotPassword.vue'
import Settings from '@/components/Settings.vue'
import Account from '@/components/Settings/Account.vue'
import Exams from '@/components/Settings/Exams.vue'
import Purchases from '@/components/Settings/Purchases.vue'
import Share from '@/components/Settings/Share.vue'
import Quiz from '@/components/Quiz.vue'
import MockExam from '@/components/MockExam.vue'
import BuildYourOwnQuiz from '@/components/Study/BuildYourOwnQuiz.vue'
import LevelUpQuizIntro from '@/components/LevelUpQuizIntro.vue'
import MockExamIntro from '@/components/MockExamIntro.vue'
import AssignmentIntro from '@/components/Assignments/AssignmentIntro.vue'
import QuizResult from '@/components/QuizResult.vue'
import QotD from '@/components/QotD.vue'
import SharedQotD from '@/components/SharedQotD.vue'
import LiveTrivia from '@/components/LiveTrivia.vue'
import TryAQuestion from '@/components/TryAQuestion.vue'
import Review from '@/components/Review.vue'
import Notebook from '@/components/Notebook.vue'
import QuestionReview from '@/components/QuestionReview.vue'
import SSOError from '@/components/SSOError.vue'
import Gift from '@/components/Gift/Gift.vue'
import GiftPurchased from '@/components/Gift/GiftPurchased.vue'
import { userModule } from '@/store/user/module'
import { referralModule } from '@/store/referral/module'
import { isSupportWindow } from '@/utils'
import { analyticsModule } from '@/store/analytics/module'
import HeardAboutUsAsk from '@/components/HeardAboutUsAsk.vue'
import { quizModule } from '@/store/quiz/module'
import { giftModule } from '@/store/gift/module'

// Buying gifts stays hidden until it launches. Gift links redeem either way.
const isGiftPurchaseEnabled = import.meta.env.VUE_APP_GIFT_PURCHASE_ENABLED === 'true'

const routes: RouteRecordRaw[] = [
    {
        path: '/study',
        name: 'study',
        component: Study,
        meta: {
            requiresAuth: true,
            title: 'Study',
        },
    },
    {
        path: '/stats',
        name: 'stats',
        component: Stats,
        meta: {
            requiresAuth: true,
            title: 'Stats',
        },
    },
    {
        path: '/review',
        name: 'review',
        component: Review,
        meta: {
            requiresAuth: true,
            title: 'Review',
        },
    },
    {
        path: '/notebook',
        name: 'notebook',
        component: Notebook,
        meta: {
            requiresAuth: true,
            title: 'Saved',
        },
    },
    {
        path: '/question-review',
        name: 'question-review',
        component: QuestionReview,
        meta: {
            requiresAuth: true,
            title: 'Review',
        },
    },
    {
        path: '/settings',
        name: 'settings',
        component: Settings,
        meta: {
            requiresAuth: true,
            title: 'Settings',
        },
        children: [
            {
                path: 'account',
                name: 'account',
                component: Account,
                alias: [ '' ],
                meta: {
                    title: 'Account Settings',
                },
            },
            {
                path: 'exams',
                name: 'exams',
                component: Exams,
                meta: {
                    title: 'Exam Settings',
                },
            },
            {
                path: 'purchases',
                name: 'purchases',
                component: Purchases,
                meta: {
                    title: 'Purchase Settings',
                },
            },
            {
                path: 'share',
                name: 'share',
                component: Share,
                meta: {
                    title: 'Share Settings',
                },
            },
        ],
    },
    {
        path: '/referrals',
        name: 'referrals',
        redirect: { name: 'share' },
    },
    {
        path: '/try-a-question',
        name: 'try-a-question',
        component: TryAQuestion,
        meta: {
            iframeOnly: true,
            ignoreAuth: true,
        },
    },
    {
        path: '/quiz',
        name: 'quiz',
        component: Quiz,
        meta: {
            requiresAuth: true,
            title: 'Quiz',
        },
    },
    {
        path: '/mock-exam',
        name: 'mock-exam',
        component: MockExam,
        meta: {
            requiresAuth: true,
            title: 'Mock Exam',
        },
    },
    {
        path: '/build-your-own-quiz',
        name: 'byoq',
        component: BuildYourOwnQuiz,
        meta: {
            requiresAuth: true,
            title: 'Build Your Own Quiz',
        },
    },
    {
        path: '/level-up-intro',
        name: 'level-up-intro',
        alias: [ '/levelUp' ],
        component: LevelUpQuizIntro,
        meta: {
            requiresAuth: true,
            title: 'Level Up',
        },
    },
    {
        path: '/mock-exam-intro',
        name: 'mock-exam-intro',
        component: MockExamIntro,
        meta: {
            requiresAuth: true,
            title: 'Mock Exam Intro',
        },
    },
    {
        path: '/assignment-intro',
        name: 'assignment-intro',
        alias: [ '/assignment' ],
        component: AssignmentIntro,
        meta: {
            requiresAuth: true,
            title: 'Assignment Intro',
        },
    },
    {
        path: '/quiz/create',
        name: 'quiz-create',
        redirect: { name: 'byoq' },
    },
    {
        path: '/quiz/:quizId',
        name: 'quiz-result',
        component: QuizResult,
        meta: {
            requiresAuth: true,
            title: 'Quiz Result',
        },
    },
    {
        path: '/heard-about-us-ask/:quizId',
        name: 'heard-about-us-ask',
        component: HeardAboutUsAsk,
        meta: {
            requiresAuth: true,
            title: 'Hear About Us',
        },
    },
    {
        path: '/sign-in',
        name: 'sign-in',
        component: SignIn,
        alias: [ '/login' ],
        meta: {
            title: 'Sign In',
        },
    },
    {
        path: '/register',
        name: 'register',
        component: Register,
        alias: [ '/sign-up', '/activateLicense' ],
        meta: {
            ignoreAuth: true,
            title: 'Register',
        },
    },
    {
        path: '/uti-discount',
        name: 'uti-discount',
        component: UtiDiscountPage,
        meta: {
            ignoreAuth: true,
            title: 'Uti Discount Page',
        },
    },
    {
        path: '/forgot-password',
        name: 'forgot-password',
        component: ForgotPassword,
        meta: {
            title: 'Forgot Password',
        },
    },
    {
        path: '/emailAuth',
        name: 'email-auth',
        component: EmailAuth,
        meta: {
            ignoreAuth: true,
            title: 'Magic Code',
        },
    },
    {
        path: '/invite',
        name: 'invite',
        component: Invite,
        meta: {
            ignoreAuth: true,
            title: 'Invite',
        },
    },
    {
        path: '/qotd/:quizId?',
        name: 'qotd',
        component: QotD,
        meta: {
            requiresAuth: true,
            title: 'Question of the Day',
        },
    },
    {
        path: '/shared-qotd',
        name: 'shared-qotd',
        component: SharedQotD,
        alias: [ '/sharedqotd' ],
        meta: {
            ignoreAuth: true,
            title: 'Shared Question of the Day',
        },
    },
    {
        path: '/live/:gameId',
        name: 'live-trivia',
        component: LiveTrivia,
        meta: {
            requiresAuth: true,
            title: 'Live Trivia',
        },
    },
    {
        path: '/sso-error',
        name: 'ssoError',
        component: SSOError,
        meta: {
            ignoreAuth: true,
            title: 'SSO Error',
        },
    },
    {
        path: '/gift',
        name: 'gift',
        component: Gift,
        beforeEnter: () => isGiftPurchaseEnabled || { name: 'study' },
        meta: {
            ignoreAuth: true,
            title: 'Pocket Prep Gift',
        },
    },
    {
        path: '/gift/purchased',
        name: 'gift-purchased',
        component: GiftPurchased,
        beforeEnter: () => isGiftPurchaseEnabled || { name: 'study' },
        meta: {
            ignoreAuth: true,
            title: 'Pocket Prep Gift',
        },
    },
    // Live Trivia host console (host-only; the server enforces it with 403 NOT_HOST)
    {
        path: '/live/host',
        name: 'live-host',
        component: () => import('@/components/LiveTriviaHost/LiveTriviaHostCreate.vue'),
        meta: {
            requiresAuth: true,
            title: 'Live Trivia Host',
        },
    },
    {
        path: '/live/host/:gameId',
        name: 'live-host-console',
        component: () => import('@/components/LiveTriviaHost/LiveTriviaHostConsole.vue'),
        meta: {
            requiresAuth: true,
            title: 'Live Trivia Host Console',
        },
    },
    {
        path: '/:pathMatch(.*)*',
        redirect: { name: 'study', params: {} },    // Empty params: see https://github.com/vuejs/router/issues/1617
    },
]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior (to, from, savedPosition) {
        if (to.hash) {
            return {
                el: to.hash,
                left: 0,
                top: 70,
            }
        }
        if (savedPosition) {
            return savedPosition
        }

        return { x: 0, y: 0 }
    },
    routes,
})

router.beforeEach((to, from, next) => {
    if (to.name === 'quiz') {
        to.meta.fromName = from.name
    }
    if (to.name === 'qotd') {
        to.meta.fromName = from.name
    }
    if (to.name === 'mock-exam') {
        to.meta.fromName = from.name
    }

    if (import.meta.env.VUE_APP_GOOGLE_ANALYTICS_ID && !isSupportWindow()) {
        gtag(
            'config',
            import.meta.env.VUE_APP_GOOGLE_ANALYTICS_ID,
            { page_path: `/${to.fullPath}` }
        )
    }

    // clear out recommended next when leaving quiz results
    const recommendedNextString = localStorage.getItem('recommendedNext')
    if (recommendedNextString && to.name !== 'quiz-result' && to.name !== 'question-review' && to.name !== 'quiz') {
        quizModule.actions.clearRecommendedNext()
    }

    // update page title
    const metaTitle = typeof to.meta?.title === 'string' ? to.meta?.title : ''
    document.title = `${metaTitle || 'Study'} | Pocket Prep`
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
    const ignoreAuth = to.matched.some(record => record.meta.ignoreAuth)
    /** If true, route is only allowed to be in an iframe */
    const iframeOnly = to.matched.some(record => record.meta.iframeOnly)
    const hasQuerySession = !!to.query.userData

    // If user is not signed in and route requires auth, completely sign out and redirect to sign-in
    // If user is signed in and route does not require auth, redirect to study
    // (NOTE: /register and /invite allow both signed in and not signed in users)
    // Otherwise, continue as usual
    const currentUser = userModule.state.user
    // at the moment this is only for try-a-question, and might not be necessary in the future
    if (iframeOnly && window.self === window.top) {
        next({ name: 'study' })
    } else if (ignoreAuth || hasQuerySession) {
        next()
    } else if (requiresAuth && !currentUser) {
        next({ name: 'sign-in', query: { redirect: to.fullPath } })
    } else if (to.name === 'sign-in' && typeof to.query.license === 'string') {
        next()
    } else if (
        to.name !== 'exams'
        && currentUser
        && (typeof to.query.license === 'string' || typeof to.query.groupCode === 'string')
    ) {
        next({ name: 'exams', query: { ...to.query } })
    } else if (!requiresAuth && currentUser) {
        next({ name: 'study', query: { ...to.query } })
    } else {
        next()
    }
})

router.afterEach((to, from, failure) => {
    // InnerTrends custom attribution tracking
    if (import.meta.env.VUE_APP_ATTRIBUTION_URL && window.innrCollectData && !failure) {
        window.innrCollectData()
    }

    // Amplitude screens
    if (to.fullPath !== from.fullPath) {
        analyticsModule.actions.amplitudeTrackScreen(to, from)
    }

    // Amplitude Deep Link event
    if (to.path === '/levelUp') {
        analyticsModule.actions.amplitudeTrack(
            'Deep Link Received',
            {
                path: to.path,
            }
        )
    }

    // Keep the referral query param in the url if we haven't consumed it yet
    const validReferral = referralModule.getters.getValidReferral()
    if (to.name && !to.query?.referral && validReferral) {
        router.replace({
            name: to.name,
            query: {
                ...to.query,
                referral: 'id' in validReferral ? validReferral.id : validReferral.objectId,
            },
        })
    }

    // Keep the gift query param in the url if we haven't consumed it yet.
    const redemptionToken = giftModule.getters.getRedemptionToken()
    if (!failure && to.name && !to.query?.redemptionToken && redemptionToken) {
        router.replace({
            name: to.name,
            params: to.params,
            query: {
                ...to.query,
                redemptionToken,
            },
        })
    }
})

export const isHeadVisible = () => {
    const routeName = router.currentRoute.value.name
    return routeName && typeof routeName === 'string' && ![
        'byoq',
        'mock-exam-intro',
        'level-up-intro',
        'assignment-intro',
        'mock-exam',
        'quiz',
        'qotd',
        'shared-qotd',
        'live-trivia',
        'quiz-result',
        'try-a-question',
        'question-review',
        'heard-about-us-ask',
        'gift',
        'gift-purchased',
        'live-host',
        'live-host-console',
    ].includes(routeName)
}

export {
    router,
    routes,
}
